import {useCallback, useState, useMemo} from 'react';
import { ReactComponent as Arc } from '../../../images/arc.svg';
import { useScreenContext } from '@context/Screen/ScreenContext';
import defaultImg from '@images/default.jpg';
import { FaInfoCircle } from 'react-icons/fa';
import { useSpecificationContext } from '@context/Specification';
import { formatPriceValue, formatMileageValue } from '@utils';
import clsx from "clsx";

const imageSizes = {
    small: 300,
    medium: 480,
    large: 600
};

const ResultCard = ({ setIsShowAdjustedModal, result, url }) => {
    const { setCarId } = useSpecificationContext();

    /**
     * Creates a string with the brand and model properties.
     *
     * @param {object} result The result object.
     * @returns {string}
     */
    const displayName = result => `${result.brand} ${result.model}`;

    /**
     * Creates a src value to get an image from the WhiteLabel API
     * with a specified format, quality and width.
     *
     * @param {number} size The width of the image in pixels.
     * @param {string} format The format of the image.
     * @param {number} quality The quality of the image.
     * @returns {string}
     */
    const imageUrl = (size, format = 'webp', quality = 75) =>
        `${result.imageUrls[0]}?fm=${format}&q=${quality}&w=${size}`;

    /**
     * Create a srcSet value based on sizes in the imageSizes array.
     * Uses imageUrl to create the urls.
     *
     * @var {string}
     */
    const srcSet = Object.values(imageSizes)
        .map(size => `${imageUrl(size)} ${size}w`)
        .join(',');

    /**
     * Open the specifications modal based on the currently selected car.
     */
    const handleSpecificationsClick = useCallback(() => {
        setCarId(result.id.carId);
    }, [result, setCarId]);

    /**
     * Open the adjusted modal
     */
    const handleAdjustedClick = (adjustment) => {
        setIsShowAdjustedModal(prevState => ({
            ...prevState,
            adjustment_state:       adjustment,
            adjustment_modal:       true
        }));
    }


    const screenSize = useScreenContext();

    const isMobile = useMemo(() => screenSize === 'mobile', [screenSize]);

    return (
        <div className={clsx(
                 `op-card`,
                 isMobile && `op-card__mobile`
             )}>
            <div className="op-card__inner">
                <div className="op-card__media">
                    <a className="op-card__image" href={url}>
                        {(result.extraFields.isAdapted === true && ((new Date().getFullYear() - result.year) <= 5)) &&
                            <span class="op-label-private-lease">Lease mogelijk</span>
                        }
                        {result.imageUrls.length !== 0 ? (
                            <img
                                src={imageUrl(imageSizes.small)}
                                srcSet={srcSet}
                                alt={displayName(result)}
                            />
                        ) : (
                            <img src={defaultImg} alt={displayName(result)} />
                        )}
                        <Arc />
                    </a>
                    {result.extraFields.isAdapted === true ? (
                        <button style={{display: "block"}} onClick={() => handleAdjustedClick(true)} className="op-card__label label-result adjusted">
                                <span>Aangepast</span>
                                <FaInfoCircle />
                            </button>
                    ) : (
                        <button style={{display: "block"}} onClick={() => handleAdjustedClick(false)} className="op-card__label label-result adaptable">
                                <span>Aanpasbaar</span>
                                <FaInfoCircle />
                            </button>
                    )}
                </div>
                <a className="op-card__content op-card__link" href={url}>
                    <h4 className="op-card__title">
                        {displayName(result)}
                    </h4>
                    <p className="op-card__subtitle">{result.type}</p>
                    <p className="op-card__type">
                        {[
                            formatMileageValue(result.mileage),
                            result.year,
                            result.transmissionName,
                            result.extraFields.vehiclePurpose
                        ]
                            .filter(Boolean)
                            .join(' • ')}
                    </p>
                    <p className="op-card__price price price--card">
                        {formatPriceValue(result.price)}
                    </p>
                </a>
                { !isMobile && (
                    <div className="op-card__actions">
                        <a href={url} className={`op-card__button btn btn-${result.extraFields.isAdapted === true ? 'adjusted' : 'adaptable'}`}>
                            Bekijk deze auto
                        </a>

                        {/*<button*/}
                        {/*    className="op-card__specs"*/}
                        {/*    onClick={handleSpecificationsClick}*/}
                        {/*>*/}
                        {/*    Specificaties <FaInfoCircle />*/}
                        {/*</button>*/}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ResultCard;
